import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Search,
  Calendar,
  AlertTriangle,
  Loader2,
  ClipboardList,
  Circle,
  Zap,
  CheckCircle2,
  RotateCcw,
} from 'lucide-react';
import { fetchGroups } from '../Users/api';
import { performanceApi } from './api';
import { periodsApi } from './services/periodsApi';

// ---------------------------------------------------------------------
// Wired to the real backend: GET /performance-periods/ (see
// groups/views/performance_periods.py). It returns every performance
// period, across all groups, in the given last-meeting date range, for
// all statuses — status is still filtered client-side below so the 4
// status cards can each show their own count.
// ---------------------------------------------------------------------

const TYPE_LABELS = { sf: 'School Fees', regular: 'Regular' };

const STATUS_CARDS = [
  { key: 'all', label: 'All', icon: ClipboardList },
  { key: 'attention', label: 'Needs Attention', icon: AlertTriangle },
  { key: 'open', label: 'Open', icon: Circle },
  { key: 'active', label: 'Active', icon: Zap },
  { key: 'closed', label: 'Closed', icon: CheckCircle2 },
];

const matchesStatus = (status, cardKey) => {
  if (cardKey === 'all') return true;
  return status === cardKey;
};

const CARD_ACCENT = {
  all: {
    icon: (isDark) => (isDark ? 'text-gray-300' : 'text-gray-600'),
    active: (isDark) => (isDark ? 'border-blue-500 bg-blue-900/20' : 'border-blue-500 bg-blue-50'),
  },
  open: {
    icon: (isDark) => (isDark ? 'text-blue-400' : 'text-blue-600'),
    active: (isDark) => (isDark ? 'border-blue-500 bg-blue-900/20' : 'border-blue-500 bg-blue-50'),
  },
  active: {
    icon: (isDark) => (isDark ? 'text-green-400' : 'text-green-600'),
    active: (isDark) => (isDark ? 'border-green-500 bg-green-900/20' : 'border-green-500 bg-green-50'),
  },
  closed: {
    icon: (isDark) => (isDark ? 'text-gray-400' : 'text-gray-500'),
    active: (isDark) => (isDark ? 'border-gray-500 bg-gray-700/40' : 'border-gray-400 bg-gray-100'),
  },
  attention: {
    icon: (isDark) => (isDark ? 'text-red-400' : 'text-red-600'),
    active: (isDark) => (isDark ? 'border-red-500 bg-red-900/20' : 'border-red-500 bg-red-50'),
  },
};

// Default date range: past 45 days, filtered against last-meeting date.
const getPast45DaysRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 45);
  return {
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0],
  };
};

const PerformancePeriods = ({ isDark = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // When PerformanceAnalysis.js's back button brought us here, it echoes
  // back exactly what we sent it on the way out (see handleViewPerformance
  // below): which row to highlight, and the full filter set that was
  // active at the time — so "back" restores the view as it was, not a
  // freshly-defaulted one.
  const returnState = location.state || {};
  const [highlightedPeriodId, setHighlightedPeriodId] = useState(
    returnState.highlightPeriodId || null
  );
  const [searchQuery, setSearchQuery] = useState(returnState.filters?.searchQuery ?? '');
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  // Only the very first load may swap the page for the full-screen spinner.
  // Later fetches (search widening the date range, date filters, reopen)
  // must keep the page mounted, or the search box is destroyed mid-typing
  // and loses focus.
  const [hasLoaded, setHasLoaded] = useState(false);
  const latestRequestRef = useRef(0);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState(returnState.filters?.statusFilter ?? 'all'); // driven by the cards
  const [typeFilter, setTypeFilter] = useState(returnState.filters?.typeFilter ?? 'all'); // plain dropdown here
  const [officerFilter, setOfficerFilter] = useState(returnState.filters?.officerFilter ?? 'all');
  const [dateRange, setDateRange] = useState(returnState.filters?.dateRange ?? getPast45DaysRange());
  const [activeDatePreset, setActiveDatePreset] = useState(returnState.filters?.activeDatePreset ?? 'past45'); // 'past45' | 'month' | 'custom' | 'all'
  const [selectedMonth, setSelectedMonth] = useState(returnState.filters?.selectedMonth ?? '');
  const [monthMenuAnchor, setMonthMenuAnchor] = useState(null);
  const [customDateDialogOpen, setCustomDateDialogOpen] = useState(false);
  const [tempDateRange, setTempDateRange] = useState({ startDate: '', endDate: '' });

  // Reopen flow: canReopen (from the backend, see PerformancePeriodListSerializer)
  // gates the button — only ever true for the one most-recently-closed
  // period per group+account-type chain. confirmReopenPeriod holds the
  // period pending confirmation in the modal below; reopeningId tracks an
  // in-flight request so the row's button can show a spinner/disable.
  const [confirmReopenPeriod, setConfirmReopenPeriod] = useState(null);
  const [reopeningId, setReopeningId] = useState(null);
  const [reopenError, setReopenError] = useState(null);

  useEffect(() => {
    loadPerformancePeriods();
  }, [dateRange]);

  // Fetches ALL statuses within the last-meeting date range — status is
  // filtered client-side so the 4 cards can each show their own count.
  const loadPerformancePeriods = async () => {
    // The page stays interactive during a fetch now, so filters can change
    // while one is in flight — only the newest request may update state.
    const requestId = ++latestRequestRef.current;
    setLoading(true);
    setError(null);
    try {
      const response = await periodsApi.fetchPerformancePeriods({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });
      if (requestId !== latestRequestRef.current) return;
      setPeriods(response);
      setHasLoaded(true);
    } catch (err) {
      if (requestId !== latestRequestRef.current) return;
      console.error('Error loading performance periods:', err);
      setError('Failed to load performance periods');
    } finally {
      if (requestId === latestRequestRef.current) setLoading(false);
    }
  };

  const handleCustomDateApply = () => {
    setDateRange(tempDateRange);
    setSelectedMonth('');
    setActiveDatePreset('custom');
    setCustomDateDialogOpen(false);
  };

  const handleClearDateFilter = () => {
    setDateRange({ startDate: '', endDate: '' });
    setSelectedMonth('');
    setActiveDatePreset('all');
  };

  const handlePast45Days = () => {
    setDateRange(getPast45DaysRange());
    setSelectedMonth('');
    setActiveDatePreset('past45');
  };

  // Searching for a group should search its whole history, not just
  // whatever falls inside the default "past 45 days" window — otherwise
  // typing a group's name could silently show nothing (or the wrong
  // periods) just because its last meeting happened to be 46+ days ago.
  // The 8-result cap in filteredPeriods below keeps this from dumping a
  // long-running group's entire history onto the screen at once.
  const prevSearchEmptyRef = useRef(searchQuery.trim() === '');
  useEffect(() => {
    const isEmpty = searchQuery.trim() === '';
    const wasEmpty = prevSearchEmptyRef.current;
    // Only on the transition from no-search to typing, and only if the
    // untouched default is still active — never override a date range
    // the user picked deliberately. If they click "Past 45 Days" again
    // afterward while still searching, that's a real re-selection (this
    // effect won't have a fresh empty->non-empty transition to react to)
    // and it sticks.
    if (wasEmpty && !isEmpty && activeDatePreset === 'past45') {
      setDateRange({ startDate: '', endDate: '' });
      setActiveDatePreset('all');
    }
    prevSearchEmptyRef.current = isEmpty;
  }, [searchQuery, activeDatePreset]);

  // Wait for the table to actually have rows before trying to scroll —
  // periods load asynchronously, so the row may not exist in the DOM yet
  // on the render right after highlightedPeriodId is set from location.state.
  useEffect(() => {
    if (!highlightedPeriodId || loading) return;
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-period-row-id="${highlightedPeriodId}"]`);
      const container = document.querySelector('main');
      if (el && container) {
        // Scroll ONLY the app's main content container, never the window.
        // el.scrollIntoView() walks up and scrolls every scrollable
        // ancestor it finds to bring el into view — which can include the
        // browser window itself. That's the actual source of the "stuck
        // scroll on every page" bug: a client-side route change never
        // resets window scroll on its own, so once scrollIntoView nudges
        // it, it stays nudged on every page after, forever. Computing the
        // offset and calling container.scrollTo directly keeps this
        // entirely inside main, where App.js's route-change reset can
        // reliably zero it out again on the next navigation.
        const elRect = el.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const offset = elRect.top - containerRect.top - (containerRect.height / 2) + (elRect.height / 2);
        container.scrollTo({ top: container.scrollTop + offset, behavior: 'smooth' });
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [highlightedPeriodId, loading, periods]);

  // Clear the highlight after a few seconds so it doesn't linger forever.
  useEffect(() => {
    if (!highlightedPeriodId) return;
    const timer = setTimeout(() => setHighlightedPeriodId(null), 3000);
    return () => clearTimeout(timer);
  }, [highlightedPeriodId]);

  // Goes straight to PerformanceAnalysis (same route App.js already maps
  // group-performance/:groupId to) — no detour through the Groups page's
  // "generate analysis" dialog, since this period already exists and was
  // already generated/closed. period_id is the important bit: it tells
  // the backend (group_performance_analysis) to resolve this one exact
  // GroupPerformancePeriod row instead of guessing "current period" or a
  // date range, so it can't accidentally show the wrong (e.g. sibling
  // school-fees/regular) period that happens to share the same dates.
  const handleViewPerformance = (period) => {
    if (!period?.group?.id) return;
    // Snapshot exactly what's on screen right now — PerformanceAnalysis.js
    // never reads this, it just carries it and hands it back verbatim in
    // its own back-button state, so this view is restored as it was.
    const filters = {
      searchQuery, statusFilter, typeFilter, officerFilter,
      dateRange, activeDatePreset, selectedMonth
    };
    navigate(`/management/group-performance/${period.group.id}?period_id=${period.id}`, {
      state: { returnTo: { highlightPeriodId: period.id, filters } }
    });
  };

  // Opens the confirmation modal — reopening changes a closed period back
  // to active and is admin-only server-side, so this isn't a one-click action.
  const handleRequestReopen = (period) => {
    setReopenError(null);
    setConfirmReopenPeriod(period);
  };

  const handleConfirmReopen = async () => {
    const period = confirmReopenPeriod;
    if (!period?.group?.id) return;

    setReopeningId(period.id);
    setReopenError(null);
    try {
      await performanceApi.reopenPerformancePeriod(period.group.id, { period_id: period.id });
      setConfirmReopenPeriod(null);
      // Re-fetch rather than patch state locally: reopening this period
      // can also change which OTHER period (if any) is now the
      // reopenable one in the same chain, and the backend is the source
      // of truth for that.
      await loadPerformancePeriods();
    } catch (err) {
      console.error('Error reopening performance period:', err);
      // The backend returns 403 with a message for non-admins — surface
      // whatever it says rather than a generic failure.
      setReopenError(err?.message || 'Failed to reopen this performance period.');
    } finally {
      setReopeningId(null);
    }
  };

  const daysSince = (dateString) => {
    if (!dateString) return null;
    const then = new Date(dateString);
    const now = new Date();
    return Math.max(0, Math.floor((now - then) / (1000 * 60 * 60 * 24)));
  };

  // A period is overdue for closing once it's been more than 7 days since
  // its last meeting and it's still sitting in 'open' or 'active'.
  const isOverdueForClosing = (p) => {
    if (p.status !== 'open' && p.status !== 'active') return false;
    const days = daysSince(p.lastMeetingDate);
    return days !== null && days > 7;
  };

  // Group search + type + officer + last-meeting date range — everything
  // except status, so all cards can be computed from one filtered set.
  const baseFiltered = useMemo(() => {
    let filtered = [...periods];

    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => p.group?.name?.toLowerCase().includes(query));
    }

    if (typeFilter !== 'all') {
      filtered = filtered.filter(p => p.type === typeFilter);
    }

    if (officerFilter !== 'all') {
      filtered = filtered.filter(p => p.officerName === officerFilter);
    }

    if (dateRange.startDate || dateRange.endDate) {
      filtered = filtered.filter(p => {
        if (!p.lastMeetingDate) return false;
        const date = new Date(p.lastMeetingDate);
        const start = dateRange.startDate ? new Date(dateRange.startDate) : null;
        const end   = dateRange.endDate   ? new Date(dateRange.endDate)   : null;
        if (start && end) return date >= start && date <= end;
        if (start) return date >= start;
        if (end)   return date <= end;
        return true;
      });
    }

    return filtered;
  }, [periods, searchQuery, typeFilter, officerFilter, dateRange]);

  // 'attention' isn't a real status — it's periods still open/active that
  // are overdue for closing — so it's counted/filtered separately from
  // the plain status match.
  const cardStats = useMemo(() => {
    return STATUS_CARDS.map(card => ({
      ...card,
      count: card.key === 'attention'
        ? baseFiltered.filter(p => isOverdueForClosing(p)).length
        : baseFiltered.filter(p => matchesStatus(p.status, card.key)).length,
    }));
  }, [baseFiltered]);

  const filteredPeriods = useMemo(() => {
    let result;
    if (statusFilter === 'attention') {
      result = baseFiltered.filter(p => isOverdueForClosing(p));
    } else {
      result = baseFiltered.filter(p => matchesStatus(p.status, statusFilter));
    }

    if (searchQuery.trim() !== '') {
      // Searching pulls in a group's whole history (see the date-range
      // clear above) — cap what's actually shown to its 8 most recent
      // periods, most recent first, rather than dumping everything.
      result = [...result]
        .sort((a, b) => new Date(b.lastMeetingDate) - new Date(a.lastMeetingDate))
        .slice(0, 8);
    }

    return result;
  }, [baseFiltered, statusFilter, searchQuery]);

  const officerOptions = useMemo(() => {
    const set = new Set();
    periods.forEach(p => { if (p.officerName) set.add(p.officerName); });
    return Array.from(set).sort();
  }, [periods]);

  // Requested format: "2 Aug 26"
  const formatShortDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: '2-digit',
    });
  };

  const getTypeBadgeColor = (type) => {
    if (type === 'sf') return isDark ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-100 text-indigo-800';
    return isDark ? 'bg-teal-900/40 text-teal-300' : 'bg-teal-100 text-teal-800';
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'open': return isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-800';
      case 'active': return isDark ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-800';
      case 'closed': return isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800';
      default: return isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800';
    }
  };

  if (loading && !hasLoaded) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className={`ml-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
          Loading performance periods...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`rounded-lg p-6 ${isDark ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
        <p className={isDark ? 'text-red-400' : 'text-red-600'}>{error}</p>
        <button
          onClick={loadPerformancePeriods}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="pb-32">
      {/* Status cards - All / Needs Attention / Open / Active / Closed */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        {cardStats.map((card) => {
          const Icon = card.icon;
          const isActive = statusFilter === card.key;
          const accent = CARD_ACCENT[card.key];
          const shout = card.key === 'attention' && card.count > 0;
          return (
            <button
              key={card.key}
              onClick={() => setStatusFilter(card.key)}
              className={`text-left rounded-lg border p-4 transition-colors ${
                isActive
                  ? accent.active(isDark)
                  : shout
                    ? isDark
                      ? 'border-red-700 bg-red-900/10 hover:border-red-500'
                      : 'border-red-300 bg-red-50 hover:border-red-400'
                    : isDark
                      ? 'border-gray-700 bg-[#2b323c] hover:border-gray-600'
                      : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-4 h-4 ${accent.icon(isDark)}`} />
                <span className={`text-xs font-medium uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {card.label}
                </span>
              </div>
              <div className={`text-2xl font-bold ${
                shout
                  ? isDark ? 'text-red-400' : 'text-red-600'
                  : isDark ? 'text-white' : 'text-gray-900'
              }`}>
                {card.count}
              </div>
            </button>
          );
        })}
      </div>

      {/* Main panel: title, filters, table and footer count all in one card. */}
      <div className={`rounded-lg shadow-sm border overflow-hidden ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'}`}>
        {/* Title */}
        <div className={`px-5 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Performance Periods
          </h2>
          <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            All groups, filtered by last meeting date.
          </p>
        </div>

        {/* Filters */}
        <div className={`flex flex-wrap items-center gap-3 px-5 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="relative flex-1 min-w-[220px]">
            <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            {loading && (
              <Loader2 className="absolute right-3 top-1/2 -mt-2 w-4 h-4 animate-spin text-blue-600" />
            )}
            <input
              type="text"
              placeholder="Search by group name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full pl-10 pr-9 py-2 border rounded-lg transition-colors ${
                isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 placeholder-gray-500 focus:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
          </div>

          {/* Type Filter */}
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className={`px-4 py-2 border rounded-lg transition-colors ${
              isDark
                ? 'bg-[#2b323c] border-gray-700 text-gray-200 focus:border-blue-500'
                : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="all">All Types</option>
            <option value="sf">School Fees (SF)</option>
            <option value="regular">Regular</option>
          </select>

          {/* Officer Filter */}
          <select
            value={officerFilter}
            onChange={(e) => setOfficerFilter(e.target.value)}
            className={`px-4 py-2 border rounded-lg transition-colors ${
              isDark
                ? 'bg-[#2b323c] border-gray-700 text-gray-200 focus:border-blue-500'
                : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="all">All Officers</option>
            {officerOptions.map(officer => (
              <option key={officer} value={officer}>
                {officer}
              </option>
            ))}
          </select>

          {/* Past 45 Days - default range */}
          <button
            onClick={handlePast45Days}
            className={`px-4 py-2 border rounded-lg transition-colors flex items-center justify-center gap-2 ${
              activeDatePreset === 'past45'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                : isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 hover:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-900 hover:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <Calendar className="w-4 h-4" />
            Past 45 Days
          </button>

          {/* Month Selector Button - filters by last meeting date */}
          <button
            onClick={(e) => setMonthMenuAnchor(e.currentTarget)}
            className={`px-4 py-2 border rounded-lg transition-colors text-left flex items-center justify-between ${
              activeDatePreset === 'month'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                : isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 hover:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-900 hover:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <span>
              {selectedMonth !== '' ? (() => {
                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const [year, monthIndex] = selectedMonth.split('-');
                return `${monthNames[parseInt(monthIndex)]} ${year}`;
              })() : 'Select Month (Last Meeting)'}
            </span>
            <Calendar className="w-4 h-4 ml-2" />
          </button>

          {/* Custom Date Button */}
          <button
            onClick={() => {
              setTempDateRange(dateRange);
              setCustomDateDialogOpen(true);
            }}
            className={`px-4 py-2 border rounded-lg transition-colors flex items-center justify-center gap-2 ${
              activeDatePreset === 'custom'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                : isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 hover:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-900 hover:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <Calendar className="w-4 h-4" />
            Custom
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`border-b ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  TYPE
                </th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  LAST MEETING
                </th>
                <th className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  STATUS
                </th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  GROUP NAME
                </th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  OFFICER
                </th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  ACTION
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
              {filteredPeriods.map((p) => (
                <tr
                  key={p.id}
                  data-period-row-id={p.id}
                  className={
                    highlightedPeriodId === p.id
                      ? (isDark ? 'bg-blue-900/30 transition-colors duration-500' : 'bg-blue-50 transition-colors duration-500')
                      : (isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50')
                  }
                >
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold uppercase ${getTypeBadgeColor(p.type)}`}>
                      {TYPE_LABELS[p.type] || p.type}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                      {formatShortDate(p.lastMeetingDate)}
                    </div>
                    <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                      {daysSince(p.lastMeetingDate)} days ago
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="inline-flex items-center gap-1.5">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold uppercase ${getStatusBadgeColor(p.status)}`}>
                        {p.status}
                      </span>
                      {isOverdueForClosing(p) && (
                        <span
                          title="Should be closed — more than 7 days since the last meeting"
                          className="inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-extrabold text-white bg-red-600 shadow-[0_0_0_2px_rgba(220,38,38,0.35)] animate-pulse cursor-help"
                        >
                          !
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                      {p.group?.name || 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                      {p.officerName || 'System'}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleViewPerformance(p)}
                        className="px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        View Performance
                      </button>
                      {p.canReopen && (
                        <button
                          onClick={() => handleRequestReopen(p)}
                          disabled={reopeningId === p.id}
                          title="Reopen this closed period"
                          className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                            isDark
                              ? 'bg-gray-700 text-amber-300 hover:bg-gray-600'
                              : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {reopeningId === p.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <RotateCcw className="w-4 h-4" />
                          )}
                          Reopen
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredPeriods.length === 0 && !loading && (
          <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <AlertTriangle className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <p className="text-lg">No performance periods found matching your filters.</p>
          </div>
        )}

        {/* Footer count */}
        <div className={`px-5 py-3 border-t text-sm ${isDark ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-600'}`}>
          Showing {filteredPeriods.length} of {periods.length} performance periods
        </div>
      </div>

      {/* Month Selection Menu */}
      {monthMenuAnchor && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-20">
          <div className="absolute inset-0 bg-black/20" onClick={() => setMonthMenuAnchor(null)}></div>
          <div className={`relative z-10 rounded-lg shadow-xl border min-w-[200px] max-h-[500px] overflow-y-auto ${
            isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'
          }`}>
            {(() => {
              const currentDate = new Date();
              const currentYear = currentDate.getFullYear();
              const currentMonth = currentDate.getMonth();
              const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

              const months = [];
              for (let i = 0; i < 12; i++) {
                const date = new Date(currentYear, currentMonth - i, 1);
                const monthIndex = date.getMonth();
                const year = date.getFullYear();
                months.push({
                  label: `${monthNames[monthIndex]} ${year}`,
                  monthIndex: monthIndex,
                  year: year,
                  value: `${year}-${monthIndex}`,
                  isCurrent: i === 0
                });
              }

              return months.map((month) => (
                <button
                  key={month.value}
                  onClick={() => {
                    const startDate = new Date(month.year, month.monthIndex, 1);
                    const endDate = new Date(month.year, month.monthIndex + 1, 0);
                    setDateRange({
                      startDate: startDate.toISOString().split('T')[0],
                      endDate: endDate.toISOString().split('T')[0]
                    });
                    setSelectedMonth(month.value);
                    setActiveDatePreset('month');
                    setMonthMenuAnchor(null);
                  }}
                  className={`w-full px-4 py-2 text-left transition-colors flex items-center justify-between ${
                    selectedMonth === month.value
                      ? isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-50 text-blue-700'
                      : isDark ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-50 text-gray-900'
                  }`}
                >
                  <span>{month.label}</span>
                  {month.isCurrent && (
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      isDark ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700'
                    }`}>
                      Current
                    </span>
                  )}
                </button>
              ));
            })()}
            <div className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={handleClearDateFilter}
                className={`w-full px-4 py-2 text-left transition-colors ${
                  isDark ? 'hover:bg-gray-700 text-red-400' : 'hover:bg-gray-50 text-red-600'
                }`}
              >
                Clear Filter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Date Range Dialog */}
      {customDateDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setCustomDateDialogOpen(false)}></div>
          <div className={`relative z-10 rounded-lg shadow-xl max-w-md w-full ${
            isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white'
          }`}>
            <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Custom Date Range
              </h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Start Date
                </label>
                <input
                  type="date"
                  value={tempDateRange.startDate}
                  onChange={(e) => setTempDateRange({ ...tempDateRange, startDate: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    isDark
                      ? 'bg-[#1e252d] border-gray-700 text-gray-200'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  End Date
                </label>
                <input
                  type="date"
                  value={tempDateRange.endDate}
                  onChange={(e) => setTempDateRange({ ...tempDateRange, endDate: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    isDark
                      ? 'bg-[#1e252d] border-gray-700 text-gray-200'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
            </div>
            <div className={`px-6 py-4 border-t flex justify-end gap-2 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => setCustomDateDialogOpen(false)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  isDark
                    ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleClearDateFilter}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                Clear
              </button>
              <button
                onClick={handleCustomDateApply}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reopen Confirmation Dialog */}
      {confirmReopenPeriod && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => { setConfirmReopenPeriod(null); setReopenError(null); }}
          ></div>
          <div className={`relative z-10 rounded-lg shadow-xl max-w-md w-full ${
            isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white'
          }`}>
            <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-lg font-semibold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                <RotateCcw className={`w-5 h-5 ${isDark ? 'text-amber-300' : 'text-amber-600'}`} />
                Reopen Performance Period
              </h3>
            </div>
            <div className="px-6 py-4">
              <p className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                Reopen the {TYPE_LABELS[confirmReopenPeriod.type] || confirmReopenPeriod.type} period for{' '}
                <span className="font-medium">{confirmReopenPeriod.group?.name}</span> (last meeting{' '}
                {formatShortDate(confirmReopenPeriod.lastMeetingDate)})? It will go back to
                Active so late payments can be recorded against it.
              </p>
              {reopenError && (
                <div className={`mt-3 px-3 py-2 rounded-lg text-sm ${
                  isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-700'
                }`}>
                  {reopenError}
                </div>
              )}
            </div>
            <div className={`px-6 py-4 border-t flex justify-end gap-2 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => { setConfirmReopenPeriod(null); setReopenError(null); }}
                disabled={reopeningId === confirmReopenPeriod.id}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  isDark
                    ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                } disabled:opacity-50`}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmReopen}
                disabled={reopeningId === confirmReopenPeriod.id}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-600 text-white hover:bg-amber-700 transition-colors disabled:opacity-50"
              >
                {reopeningId === confirmReopenPeriod.id && <Loader2 className="w-4 h-4 animate-spin" />}
                Reopen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformancePeriods;